import { PersistConfig, persistReducer } from 'redux-persist';
import { createReducer, getType } from 'typesafe-actions';

import { StreamingPalActions } from '@lib/state/_actions';
import { produce } from 'immer';
import storage from '@lib/state/storage';
import autoMergeLevel2 from 'redux-persist/lib/stateReconciler/autoMergeLevel2';
import { Album, AlbumlOption } from '@lib/domain/streamingpal';

import { StreamingpalState, UserInfo, Track } from './types';

const stateKey = 'streamingpals';

/* ------------- Initial State ------------- */
const INITIAL_STATE: StreamingpalState = {
    userInfo: {
        name: '',
        image: '',
        id: '',
        type: '',
        url: '',
    },
    listAlbum: null,
    urlAlbumSelected: null,
    currentAlbum: null,
    tracks: [],
    albumType: '',
};

/* ------------- Reducers ------------- */
const setUserInfo = (
    state: StreamingpalState,
    {
        payload: { userInfo },
    }: ReturnType<typeof StreamingPalActions.setUserInfo>,
): StreamingpalState =>
    produce(state, (draft) => {
        Object.assign(draft, INITIAL_STATE, { userInfo });
    });

const setListAlbum = (
    state: StreamingpalState,
    {
        payload: { listAlbum },
    }: ReturnType<typeof StreamingPalActions.setListAlbum>,
): StreamingpalState =>
    produce(state, (draft) => {
        draft.listAlbum = listAlbum;
    });

const setUrlAlbumSelected = (
    state: StreamingpalState,
    {
        payload: { urlAlbum, currentAlbum },
    }: ReturnType<typeof StreamingPalActions.setUrlAlbumSelected>,
): StreamingpalState =>
    produce(state, (draft) => {
        draft.urlAlbumSelected = urlAlbum;
        draft.currentAlbum = currentAlbum;
    });
const setTracks = (
    state: StreamingpalState,
    { payload: { tracks } }: ReturnType<typeof StreamingPalActions.setTracks>,
): StreamingpalState =>
    produce(state, (draft) => {
        draft.tracks = tracks;
    });
const setAlbumType = (
    state: StreamingpalState,
    {
        payload: { albumType },
    }: ReturnType<typeof StreamingPalActions.setAlbumType>,
): StreamingpalState =>
    produce(state, (draft) => {
        draft.albumType = albumType;
        draft.urlAlbumSelected = null;
        draft.tracks = [];
        draft.currentAlbum = null;
    });
/* ------------- Hookup Reducers To Types ------------- */
const reducer = createReducer(INITIAL_STATE, {
    [getType(StreamingPalActions.setUserInfo)]: setUserInfo,
    [getType(StreamingPalActions.setListAlbum)]: setListAlbum,
    [getType(StreamingPalActions.setUrlAlbumSelected)]: setUrlAlbumSelected,
    [getType(StreamingPalActions.setTracks)]: setTracks,
    [getType(StreamingPalActions.setAlbumType)]: setAlbumType,
});

const persistConfig: PersistConfig<StreamingpalState> = {
    key: stateKey,
    storage,
    stateReconciler: autoMergeLevel2,
};

const persistedReducer = persistReducer<
    StreamingpalState,
    StreamingPalActions.StreamingPalActions
>(persistConfig, reducer);

/* ------------- Selectors ------------- */
// use any for root state for now because of Dependency cycle error
// TODO: resolve dependency cycle error
// eslint-disable-next-line
const getReducerState = (state: any): StreamingpalState => state[stateKey];

const selectors = {
    getUserInfo: (state: StreamingpalState): UserInfo => state.userInfo,
    getListAlbum: (state: StreamingpalState): Album[] => state.listAlbum,
    getUrlCurrentAlbum: (state: StreamingpalState): string =>
        state.urlAlbumSelected,
    getCurrentAlbum: (state: StreamingpalState): AlbumlOption | undefined =>
        state.currentAlbum,
    getTracks: (state: StreamingpalState): Track[] => state.tracks,
    getAlbumType: (state: StreamingpalState): string => state.albumType,
};

/* ------------- Export ------------- */
const reducerObject = {
    // default export
    selectors,

    INITIAL_STATE,

    stateKey,
    getReducerState,
    reducer: persistedReducer,
};

export default reducerObject;
